import React, { useState } from 'react';
import { theme } from '../theme';
import { computeCurrentLevel, TierIcon } from './MomentumBar';

// EngagementStrip — new (Sep 4, per Ali's report). Real layout problem
// found live from a screenshot: trip cards (the actual product) sat
// below 8 stacked blocks, requiring a full scroll to reach. Merges what
// used to be 3 separate full-height cards (MomentumBar's achievement
// strip, the streak card, the referral card) into one compact block,
// matching Option C from a design-research pass on comparable products
// (Hopper keeps tracked trips at the top with its own deals feed below
// them; Duolingo fuses its streak into the core loop rather than
// stacking a permanent card on top of it). This is the middle ground:
// a real, informative strip on first look, collapsible by the user's
// own choice (not an automatic session-count guess, which would feel
// like the app rearranging itself) to a single line once they've seen
// it enough times to know it's there.
const COLLAPSE_STORAGE_KEY = 'flyatlast_engagement_strip_collapsed';

function readCollapsedState() {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    // localStorage can throw in private browsing / disabled-storage
    // contexts — default to the full, informative view rather than
    // fail the whole component.
    return false;
  }
}

function writeCollapsedState(value) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Best-effort — if it can't persist, the toggle still works for
    // this session via React state, it just won't stick next visit.
  }
}

function EngagementStrip({ achievements, referralStats, onShare, shareLabel, tiles }) {
  const [collapsed, setCollapsed] = useState(readCollapsedState);

  const setCollapsedPersisted = (value) => {
    setCollapsed(value);
    writeCollapsedState(value);
  };

  const levelResult = computeCurrentLevel(achievements);
  const streakDays = referralStats?.current_streak_days || 0;
  const streakMilestoneSize = referralStats?.streak_milestone_size || 7;
  const hasReferral = !!referralStats?.referral_code;
  const referralMilestoneSize = referralStats?.milestone_size || 1;
  const referralProgress = Math.min(referralStats?.milestone_progress || 0, referralMilestoneSize);

  if (tiles) {
    // Compact inline stats (Oct 10, Option B) — one thin row inside the
    // Dashboard panel; styling comes from the .dash-stat classes there.
    const daysIntoCycle = streakDays === 0 ? 0 : ((streakDays % streakMilestoneSize === 0) ? streakMilestoneSize : streakDays % streakMilestoneSize);
    const dot = (on) => ({ width: '11px', height: '11px', borderRadius: '50%', background: on ? '#1E3A5F' : '#fff', border: '1px solid #1E3A5F' });
    return (
      <>
        {(
          <div className="dash-stat">
            <span className="dash-lbl">Streak</span>
            <b>{streakDays > 0 ? `${streakDays} day${streakDays === 1 ? '' : 's'}` : 'None yet'}</b>
            <span style={{ display: 'inline-flex', gap: '3px' }}>
              {Array.from({ length: streakMilestoneSize }, (_, i) => i + 1).map(day => (
                <span key={day} style={dot(day <= daysIntoCycle)} />
              ))}
            </span>
          </div>
        )}
        {hasReferral && (
          <div className="dash-stat">
            <span className="dash-lbl">Referral</span>
            <b style={{ fontWeight: 700 }}>
              {referralMilestoneSize === 1 ? 'Refer 1 friend, get 1 bonus pin' : `Refer ${referralMilestoneSize} friends, get a bonus pin`}
            </b>
            <span style={{ display: 'inline-flex', gap: '3px' }}>
              {Array.from({ length: referralMilestoneSize }, (_, i) => i + 1).map(spot => (
                <span key={spot} style={dot(spot <= referralProgress)} />
              ))}
            </span>
            <button
              onClick={onShare}
              style={{ background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '0px', padding: '5px 10px', fontSize: '12px', fontWeight: 800, fontFamily: theme.fontDisplay, cursor: 'pointer', whiteSpace: 'nowrap', borderRadius: '8px' }}
            >
              {shareLabel}
            </button>
          </div>
        )}
      </>
    );
  }

  if (!levelResult && streakDays === 0 && !hasReferral) return null;

  if (collapsed) {
    return (
      <div style={{
        background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px',
        padding: '9px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap'
      }}>
        {levelResult && (
          <span style={{ fontSize: '12px', color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: '5px' }}>
            <TierIcon type={levelResult.currentLevel.icon} color={theme.structural} />
            {levelResult.currentLevel.label}
          </span>
        )}
        {streakDays > 0 && (
          <span style={{ fontSize: '12px', color: theme.textPrimary }}>{streakDays}-day streak</span>
        )}
        {hasReferral && (
          <span style={{ fontSize: '12px', color: theme.textPrimary, flex: 1, minWidth: '140px' }}>
            {referralMilestoneSize === 1 ? 'Refer a friend for a bonus pin' : `Refer ${referralMilestoneSize} friends for a bonus pin`}
          </span>
        )}
        <span
          onClick={() => setCollapsedPersisted(false)}
          style={{ fontSize: '12px', color: theme.textMuted, textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          View →
        </span>
      </div>
    );
  }

  return (
    <div style={{ background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px', padding: '16px 40px 16px 18px', marginBottom: '14px', position: 'relative' }}>
      <span
        onClick={() => setCollapsedPersisted(true)}
        role="button"
        aria-label="Collapse"
        style={{ position: 'absolute', top: '12px', right: '14px', cursor: 'pointer', color: theme.textMuted, fontSize: '15px', lineHeight: 1, fontWeight: 700 }}
      >
        ×
      </span>

      {levelResult && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px' }}>
          <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: theme.structural, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TierIcon type={levelResult.currentLevel.icon} color="#fff" />
          </div>
          <span style={{ fontSize: '12.5px', fontWeight: 700, color: theme.textPrimary }}>{levelResult.currentLevel.label}</span>
        </div>
      )}

      {/* Rebalanced (Sep 4, per Ali's report — real layout problem
          caught from a screenshot). Was a single flex row (streak /
          referral / share button) with `flex: 1` on the referral block
          — that pushed the button off to the far right with a large,
          unbalanced gap in between, since the referral copy itself is
          short and didn't fill the space it was claiming. Now a real
          2-column grid when both streak and referral are present, with
          a divider between them, and the share button anchored directly
          under its own referral column instead of floating separately
          at the row's edge. Falls back to one full-width column
          (no divider) when only one of the two exists. */}
      {(() => {
        const showStreak = streakDays > 0;
        const showBothColumns = showStreak && hasReferral;
        return (
          <div style={{
            display: 'grid',
            gridTemplateColumns: showBothColumns ? '1fr 1fr' : '1fr',
            alignItems: 'start'
          }}>
            {showStreak && (() => {
              const daysIntoCycle = (streakDays % streakMilestoneSize === 0) ? streakMilestoneSize : streakDays % streakMilestoneSize;
              return (
                <div style={{
                  paddingRight: showBothColumns ? '24px' : 0,
                  borderRight: showBothColumns ? `1px solid ${theme.divider}` : 'none'
                }}>
                  {/* Bumped from 10.5px (Sep 8, per Ali's report — real
                      complaint: this read as weak/faded against the
                      rest of the Dashboard, confirmed by comparison —
                      10.5px was one of the smallest text sizes
                      anywhere in the app, despite streak/referral being
                      the only two mechanics that actually grant
                      something real, per this file's own header
                      comment. Dots bumped to match, 16px → 20px, so
                      the enlarged label doesn't visually dwarf them. */}
                  <div style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: theme.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Streak</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: theme.structural, marginBottom: '8px' }}>{streakDays}-day streak</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {Array.from({ length: streakMilestoneSize }, (_, i) => i + 1).map(day => (
                      <div
                        key={day}
                        style={{
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: day <= daysIntoCycle ? theme.structural : theme.bg,
                          border: day <= daysIntoCycle ? 'none' : `1px solid ${theme.divider}`
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}

            {hasReferral && (
              <div style={{ paddingLeft: showBothColumns ? '24px' : 0 }}>
                {/* Same size fix as the streak label above — matching
                    treatment, matching reasoning. */}
                <div style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: theme.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Referral</div>
                <div style={{ fontSize: '17px', fontWeight: 800, color: theme.structural, marginBottom: '8px' }}>
                  {referralMilestoneSize === 1 ? 'Refer 1 friend, get 1 bonus pin' : `Refer ${referralMilestoneSize} friends, get a bonus pin`}
                </div>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                  {Array.from({ length: referralMilestoneSize }, (_, i) => i + 1).map(spot => (
                    <div
                      key={spot}
                      style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        background: spot <= referralProgress ? theme.structural : theme.bg,
                        border: spot <= referralProgress ? 'none' : `1px solid ${theme.divider}`
                      }}
                    />
                  ))}
                </div>
                <button
                  onClick={onShare}
                  style={{
                    background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '0px',
                    padding: '8px 16px', fontSize: '12px', fontWeight: 700, fontFamily: theme.fontDisplay,
                    cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  {shareLabel}
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default EngagementStrip;